import { ApplicationView, useController } from '@aha-app/mvc';
import React, { useCallback, useState } from 'react';
import { type CopiedWhiteboardContent } from '../collaboration/Serialization';
import type WhiteboardController from '../controllers/WhiteboardController';
import { type CustomShape } from '../models/CustomShape';
import styles from '../styles.module.css';
import ObjectMenuController from '../toolbars/ObjectMenu/ObjectMenuController';
import Modal from '../utils/Modal';
import { labeledMenuComponent } from '../utils/stylus';
import version from '../version';
import { ClipboardDataDrawingBrush } from './ClipboardDataDrawingBrush';
import { generatePNGFromClipboardData } from './ClipboardShapeIconGenerator';
import { CustomShapeNameModal } from './CustomShapeNameModal';
import Stylus from './Stylus';

class StylusCustomShape extends Stylus {
  customShape: CustomShape;
  icon: string | undefined;
  controller: WhiteboardController;

  constructor({
    controller,
    customShape,
  }: {
    controller: WhiteboardController;
    customShape: CustomShape;
  }) {
    super({ controller });
    this.customShape = customShape;
    this.icon = undefined;
    void generatePNGFromClipboardData(controller, customShape.objects).then(
      result => {
        if (result) {
          this.icon = result;
        }
      }
    );
  }

  renderMenuIcon() {
    if (this.icon) {
      return (
        <img
          data-testid={`custom-shape-${this.customShape.name}`}
          width='38'
          height='30'
          src={this.icon}
        ></img>
      );
    }

    return (
      <span data-testid={`custom-shape-${this.customShape.name}`}>
        {this.customShape.name}
      </span>
    );
  }

  menuComponent() {
    return labeledMenuComponent(this.renderMenuIcon(), this.customShape.name);
  }

  toolButtonClass() {
    return styles.labeledToolButton;
  }

  cursor() {
    return 'crosshair';
  }

  activate() {
    this.controller.canvas.isDrawingMode = true;
    const clipboardData: CopiedWhiteboardContent = {
      objects: this.customShape.objects,
      version,
      whiteboardId: this.controller.whiteboardId,
    };
    this.controller.canvas.freeDrawingBrush = new ClipboardDataDrawingBrush(
      clipboardData,
      this.controller,
      this
    );
  }

  stylusId() {
    return `customShape-${this.customShape.id}`;
  }

  onHoverMenu = () => {
    return (
      <HoverMenu
        shapeName={this.customShape.name}
        onDeleteConfirmed={() => {
          this.controller.dataStoreCustomShapes.removeRecord(this.customShape);
        }}
        onRenamed={(newName: string) => this.customShape.set('name', newName)}
        resetStylus={() => {
          this.controller.actionSetStylus(this.controller.stylusPointer);
        }}
      />
    );
  };
}

/** The ... menu that appears on hover in the top right corner of a custom shape  */
const HoverMenu = ApplicationView(
  ({
    shapeName,
    onDeleteConfirmed,
    onRenamed,
    resetStylus,
  }: {
    shapeName: string;
    onDeleteConfirmed: () => void;
    onRenamed: (newName: string) => void;
    /** HoverMenu introduces the possibility of doing something in the shape menu
     * that isn't about selecting a stylus: renaming or deleting custom shapes. We
     * need a way to override the normal stylus-selection logic so that if (eg) someone deletes
     * a custom shape then we don't also select it for insertion on the next click in
     * the whiteboard.
     */
    resetStylus: () => void;
  }) => {
    const [menuIsOpen, setMenuIsOpen] = useState(false);
    const [confirmDeleteModalIsOpen, setConfirmDeleteModalIsOpen] =
      useState(false);
    const [renameModalIsOpen, setRenameModalIsOpen] = useState(false);
    const controller = useController(ObjectMenuController);

    // Listen to the two menu close events from <aha-menu> so that
    // we can set the menu visibility. This way the menu stays open
    // until the user clicks outside of it or hits escape.
    const menuRef = useCallback(
      menu => {
        menu?.addEventListener('aha-menu:cancel', () => {
          setMenuIsOpen(false);
        });
        menu?.addEventListener('aha-menu:closed', () => {
          setMenuIsOpen(false);
        });
      },
      [setMenuIsOpen]
    );

    return (
      <div
        className={styles.toolButtonHoverMenu}
        style={{ display: menuIsOpen ? 'block' : undefined }}
      >
        <aha-menu
          ref={menuRef}
          data-testid='custom-shape-name-hover-menu'
          open={menuIsOpen ? true : undefined}
          onClick={e => {
            /* Stop propagation so that clicking on the hover menu doesn't 
            select the underlying shape */
            e.stopPropagation();
            setMenuIsOpen(!menuIsOpen);
            // If menuIsOpen then we are closing it and should reset the stylus
            // so that we haven't selected the stylus of the custom shape that
            // the menu sits over
            if (menuIsOpen) {
              resetStylus();
            }
          }}
        >
          <aha-menu-content>
            <aha-menu-group>
              <aha-menu-item onClick={() => setRenameModalIsOpen(true)}>
                Rename
              </aha-menu-item>
            </aha-menu-group>
            <aha-menu-group>
              <aha-menu-item
                type='danger'
                onClick={() => setConfirmDeleteModalIsOpen(true)}
              >
                Delete
              </aha-menu-item>
            </aha-menu-group>
          </aha-menu-content>
        </aha-menu>
        {confirmDeleteModalIsOpen && (
          <ConfirmDeleteModal
            onClose={result => {
              resetStylus();
              if (result === 'deleteConfirmed') {
                onDeleteConfirmed();
                controller.actionCloseMenu();
              }
              setConfirmDeleteModalIsOpen(false);
            }}
          />
        )}
        {renameModalIsOpen && (
          <CustomShapeNameModal
            kind='rename'
            initialValue={shapeName}
            onClose={newName => {
              resetStylus();
              setRenameModalIsOpen(false);
              if (newName) {
                onRenamed(newName);
                controller.actionCloseMenu();
              }
            }}
          />
        )}
      </div>
    );
  }
);

const ConfirmDeleteModal = ({
  onClose,
}: {
  onClose: (result: 'deleteConfirmed' | 'cancelled') => void;
}) => {
  // TODO: We do this in a bunch of places, should we have <AhaModalHeader>?
  // If we don't listen to the header close even then modal won't show
  // again after being closed by clicking on the X in the header, because
  // React hasn't been told that the modal closed.
  const modalHeaderRef = useCallback(
    header =>
      header?.addEventListener('aha-modal-header:close', (e: Event) => {
        // Stop propagation otherwise the shape menu will close
        e.stopPropagation();
        onClose('cancelled');
      }),
    [onClose]
  );

  // If we don't listen to the cancelled event the backdrop won't disappear
  // when the user presses escape to cancel the modal (doesn't happen in whiteboard
  // demo app but does in Aha app)
  const modalRef = useCallback(
    modal =>
      modal?.addEventListener('aha-modal:cancelled', (e: Event) => {
        // Stop propagation otherwise the shape menu will close
        e.stopPropagation();
        onClose('cancelled');
      }),
    [onClose]
  );

  return (
    <Modal
      onClose={() => {
        onClose('cancelled');
      }}
    >
      <aha-modal open={true} size='small' ref={modalRef}>
        <aha-modal-header
          onClick={e => {
            /* If we don't stop propagation then the click is treated as a click
            outside of the object menu, which closes it. Thus clicking on the X to
            cancel the modal would also close the object menu, which we don't want.
            */
            e.stopPropagation();
          }}
          ref={modalHeaderRef}
        >
          Warning
        </aha-modal-header>
        <aha-modal-body-danger>
          <div>Confirm deletion</div>
          <div slot='content'>
            Are you sure you want to delete this custom shape?
          </div>
        </aha-modal-body-danger>
        <aha-modal-footer>
          <aha-button
            slot='secondary_actions'
            onClick={e => {
              e.stopPropagation();
              onClose('cancelled');
            }}
          >
            Cancel
          </aha-button>
          <aha-button
            kind='danger'
            tabIndex={0}
            onClick={e => {
              e.stopPropagation();
              onClose('deleteConfirmed');
            }}
            data-testid='custom-shape-confirm-delete-button'
          >
            Delete
          </aha-button>
        </aha-modal-footer>
      </aha-modal>
    </Modal>
  );
};

export default StylusCustomShape;
